// web/src/components/KidPhotoUploader.tsx
// Inline photo manage UI used inside the KidEdit form. Shows the current
// photo (if any), an upload picker, and a remove button. Identical 3-step
// flow as MatchPhoto: presign → POST to S3 → PATCH kid.photoPath.

import { useEffect, useRef, useState } from 'react';
import { Button } from './Button';
import { useKidApi } from '../api/kid';
import { ApiError } from '../api/client';
import { compressForUpload } from '../lib/image-resize';

const ALLOWED = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif']);
const MAX_BYTES = 10 * 1024 * 1024;

interface Props {
  kidId: string;
  /** True when the kid record has a photoPath. */
  hasPhoto: boolean;
  /** Called after upload or removal so the caller can refresh the kid. */
  onChange: () => Promise<void> | void;
}

export function KidPhotoUploader({ kidId, hasPhoto, onChange }: Props) {
  const api = useKidApi();
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);

  // Lazy fetch the signed GET URL when a photo exists.
  useEffect(() => {
    if (!hasPhoto) { setImageUrl(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await api.photoUrl(kidId);
        if (!cancelled) setImageUrl(res.url);
      } catch { if (!cancelled) setImageUrl(null); }
    })();
    return () => { cancelled = true; };
  }, [api, kidId, hasPhoto]);

  const choose = () => fileRef.current?.click();

  const upload = async (rawFile: File) => {
    setError(null);
    if (!ALLOWED.has(rawFile.type)) { setError('JPEG, PNG, WebP or HEIC.'); return; }
    if (rawFile.size > MAX_BYTES) { setError(`Under ${Math.round(MAX_BYTES / 1024 / 1024)}MB.`); return; }
    setUploading(true);
    try {
      // Browser-side resize→WebP. Cuts upload bytes ~10–20x for phone photos.
      const file = await compressForUpload(rawFile);
      const presign = await api.presignPhoto(kidId, file.type, file.size);
      const fd = new FormData();
      for (const [k, v] of Object.entries(presign.fields)) fd.append(k, v);
      fd.append('file', file);
      const r = await fetch(presign.uploadUrl, { method: 'POST', body: fd });
      if (!r.ok) throw new Error(`Upload failed (HTTP ${r.status})`);
      await api.update(kidId, { photoPath: presign.key });
      await onChange();
    } catch (err) {
      setError((err as ApiError).message || 'Upload failed.');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const remove = async () => {
    setRemoving(true);
    setError(null);
    try {
      await api.removePhoto(kidId);
      await onChange();
    } catch (err) {
      setError((err as ApiError).message || 'Could not remove.');
    } finally {
      setRemoving(false);
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-base)',
        padding: 'var(--space-base)',
        background: 'var(--surface-2)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)',
      }}
    >
      <div
        style={{
          width: 80, height: 80,
          borderRadius: '50%',
          overflow: 'hidden',
          background: imageUrl ? '#000' : 'var(--green-light)',
          display: 'grid', placeItems: 'center',
          flexShrink: 0,
          boxShadow: 'var(--shadow-1)',
        }}
        aria-hidden
      >
        {imageUrl ? (
          <img src={imageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <span style={{ fontSize: '2rem' }}>📷</span>
        )}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 'var(--small)', color: 'var(--muted)', marginBottom: 8 }}>
          {hasPhoto ? 'Profile photo set.' : 'A photo brings the journal to life.'}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
            style={{ display: 'none' }}
            onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload(f); }}
          />
          <Button label={uploading ? 'Uploading…' : (hasPhoto ? 'Replace' : 'Upload')} onClick={choose} loading={uploading} variant="ghost" />
          {hasPhoto ? (
            <Button label={removing ? 'Removing…' : 'Remove'} onClick={remove} loading={removing} variant="ghost" style={{ color: 'var(--electric-red)' }} />
          ) : null}
        </div>
        {error ? <div style={{ color: 'var(--electric-red)', fontSize: 'var(--small)', marginTop: 6 }}>{error}</div> : null}
      </div>
    </div>
  );
}
